import { ApolloError } from "@apollo/client/errors";

/**
 * Reads the gateway's GraphQL error code so a UI can tell one failure from
 * another, and maps the auth surfaces' codes onto copy keys.
 *
 * Why this exists: the login and signup forms used to collapse every rejection
 * into one message. `LoginUserForm` said "invalid email or password" for a
 * network drop, a 500 and a bot-check rejection alike, and `RegisterUserForm`
 * rendered `FORBIDDEN` as "this email is banned". On 2026-08-17 that cost
 * hours on a preview deployment: the gateway's `@turnstile` directive was
 * rejecting every login, and the form insisted the credentials were wrong.
 *
 * ## `FORBIDDEN` does not name a cause
 *
 * Deliberately, no copy here claims one. The gateway throws `FORBIDDEN` for
 * several unrelated reasons on these two mutations:
 *
 *  - `gateway/src/directives/turnstile.ts`: no `x-turnstile-token` header, or
 *    a token Cloudflare will not verify, or a token whose action does not
 *    match the field's.
 *  - `member-resolver.service.ts` `login()`: the account is not active.
 *  - `member-resolver.service.ts` `login()`: the account has no merchant, or
 *    its merchants are deleted.
 *  - `member-resolver.service.ts` `signup()`: the email belongs to an
 *    existing but inactive account.
 *
 * The only thing separating them is the English message text, which is not
 * something a localised UI can branch on. So the code cannot tell us whether
 * the bot check failed or the account is blocked, and any copy that picks one
 * is wrong for the others. That is the bug the old "this email is banned"
 * string was: correct for the inactive-account path, false for every request
 * Turnstile rejected. The copy below stays true for all of them and gives the
 * user the one action that helps in the common case (reload and retry).
 *
 * Resolving that ambiguity needs distinct codes from the gateway, which is a
 * gateway change, not a web one. Until then `logGraphQLError`-style callers
 * should log {@link getGraphQLErrorMessage} next to the code: the message is
 * what separates the causes when someone reads the logs.
 */

/** Gateway error codes the auth surfaces branch on. */
export const GRAPHQL_ERROR_CODE = {
  /** Ambiguous: bot check, inactive account, or deleted merchant. See above. */
  FORBIDDEN: "FORBIDDEN",
  /**
   * The gateway's own code for a Turnstile rejection (gateway #835). Older
   * gateway deploys still send a bot-check rejection as `FORBIDDEN`, so every
   * surface treats the two alike via {@link isBlockedRequestCode}.
   */
  TURNSTILE_REJECTED: "TURNSTILE_REJECTED",
  /** Genuine credential rejection: unknown email, or wrong password. */
  UNAUTHENTICATED: "UNAUTHENTICATED",
  /** Signup against an address that already has an active account. */
  EMAIL_ALREADY_REGISTERED: "EMAIL_ALREADY_REGISTERED",
  /**
   * Phone verification against a number another account already holds. The
   * gateway checks this server side, behind auth, Turnstile and a per-user and
   * per-IP rate limit, and names it so the phone field can say so.
   */
  PHONE_ALREADY_REGISTERED: "PHONE_ALREADY_REGISTERED",
} as const;

/**
 * Whether a code means the request was blocked: a Turnstile rejection, or the
 * ambiguous `FORBIDDEN` that still covers one on older gateway deploys.
 */
export const isBlockedRequestCode = (code: string | undefined): boolean =>
  code === GRAPHQL_ERROR_CODE.FORBIDDEN ||
  code === GRAPHQL_ERROR_CODE.TURNSTILE_REJECTED;

/**
 * The first GraphQL error code on an Apollo error.
 *
 * `undefined` means there was no GraphQL error to read: a transport failure, a
 * thrown non-Apollo error, or anything else that never reached a resolver.
 * Callers must treat that as the unknown class, never as a credential answer.
 */
export const getGraphQLErrorCode = (error: unknown): string | undefined => {
  const { graphQLErrors } = (error as ApolloError | undefined) ?? {};
  const [graphQLError] = graphQLErrors ?? [];
  const code = graphQLError?.extensions?.code;

  return typeof code === "string" ? code : undefined;
};

/**
 * Whether any GraphQL error on an Apollo error carries this gateway code.
 * Unlike {@link getGraphQLErrorCode} it looks past the first error, for the
 * refusals a flow branches on (`CART_PRICE_CHANGED`,
 * `CHECKOUT_PAYMENT_METHOD_UNAVAILABLE`). A non-Apollo error never matches.
 */
export const hasGatewayErrorCode = (error: unknown, code: string): boolean =>
  error instanceof ApolloError &&
  error.graphQLErrors.some(
    graphQLError => graphQLError.extensions?.code === code
  );

/**
 * Whether the gateway rejected the session token itself - expired, revoked or
 * forged - which is the only failure that should end a session.
 *
 * The gateway says so two ways: an `UNAUTHENTICATED` GraphQL error, or an HTTP
 * 401 that Apollo surfaces as a network error with that status. A transport
 * failure without a status (offline, a timeout, a request aborted because the
 * user navigated away) says nothing about the token.
 */
export const isSessionRejected = (error: unknown): boolean => {
  if (getGraphQLErrorCode(error) === GRAPHQL_ERROR_CODE.UNAUTHENTICATED) {
    return true;
  }

  const { networkError } = (error as ApolloError | undefined) ?? {};
  const status = (networkError as { statusCode?: number } | null | undefined)
    ?.statusCode;

  return status === 401;
};

/**
 * The first GraphQL error message, for logs only.
 *
 * Never render this. It is unlocalised server English, and on the auth
 * mutations it leaks whether an address is registered ("Email is not
 * registered" versus "The provided password is incorrect").
 */
export const getGraphQLErrorMessage = (error: unknown): string | undefined => {
  const { graphQLErrors } = (error as ApolloError | undefined) ?? {};
  const [graphQLError] = graphQLErrors ?? [];

  return graphQLError?.message;
};

/**
 * Copy for the three answers an auth form can honestly give.
 *
 * `blocked` is the new key and the only one Lokalise has not shipped yet, so
 * every caller routes it through {@link resolveAuthErrorCopy}, which falls
 * back to `unknown` while the key is missing. `credentials` stays
 * deliberately vague about which half was wrong: distinguishing a bot-check
 * failure from a credential failure is the point of this module, telling an
 * unauthenticated caller whether an account exists is not.
 */
export const AUTH_ERROR_COPY_KEY = {
  blocked: "common:common-auth-request-blocked",
  /**
   * The bot check itself failed (`TURNSTILE_REJECTED`): the browser sent no token, or one
   * Cloudflare did not accept. Reloading rarely helps; a blocker or privacy setting usually
   * stops the check, so the copy says what does. `FORBIDDEN` keeps `blocked`.
   */
  robotCheck: "common:common-auth-robot-check-failed",
  credentials: "common:common_email_pass_invalid",
  emailAlreadyRegistered: "common:common-signup-modal-failed-existing",
  phoneAlreadyRegistered: "common:common-phone-already-registered",
  /** Lokalise plural key (`.one` / `.other`), `{{count}}` = minutes to wait. */
  rateLimited: "common:common-auth-rate-limited",
  /** Same, for a rejection that does not say how long to wait. */
  rateLimitedLater: "common:common-auth-rate-limited-later",
  signupUnknown: "common:common-signup-modal-failed-unknown-error",
  unknown: "common:common_generic_error_message",
} as const;

/** Which copy a failed `login` deserves. */
export const getLoginErrorCopyKey = (error: unknown): string => {
  switch (getGraphQLErrorCode(error)) {
    case GRAPHQL_ERROR_CODE.UNAUTHENTICATED:
      return AUTH_ERROR_COPY_KEY.credentials;
    case GRAPHQL_ERROR_CODE.TURNSTILE_REJECTED:
      return AUTH_ERROR_COPY_KEY.robotCheck;
    case GRAPHQL_ERROR_CODE.FORBIDDEN:
      return AUTH_ERROR_COPY_KEY.blocked;
    default:
      return AUTH_ERROR_COPY_KEY.unknown;
  }
};

/**
 * Which copy a failed `signup` deserves.
 *
 * There is no gateway code that means "this email is banned". A banned address
 * arrives as `FORBIDDEN`, indistinguishable from a bot-check rejection, and
 * the only place the gateway names it as such is the `checkEmailExists` query
 * (`CheckEmailExistsStatus.BANNED`), which this repo does not call. So the
 * branch that used to render the banned-email string is gone rather than left
 * pointing at the wrong cause.
 */
export const getSignupErrorCopyKey = (error: unknown): string => {
  switch (getGraphQLErrorCode(error)) {
    case GRAPHQL_ERROR_CODE.EMAIL_ALREADY_REGISTERED:
      return AUTH_ERROR_COPY_KEY.emailAlreadyRegistered;
    case GRAPHQL_ERROR_CODE.TURNSTILE_REJECTED:
      return AUTH_ERROR_COPY_KEY.robotCheck;
    case GRAPHQL_ERROR_CODE.FORBIDDEN:
      return AUTH_ERROR_COPY_KEY.blocked;
    default:
      return AUTH_ERROR_COPY_KEY.signupUnknown;
  }
};

/**
 * Which copy any other Turnstile-gated request deserves.
 *
 * For the OTP and listing mutations there is no credential class to confuse
 * `FORBIDDEN` with, so these surfaces were never actively lying. They were
 * still opaque: a bot-check rejection arrived as the same "something went
 * wrong" as a timeout, or as raw unlocalised gateway English. Naming the
 * blocked class gives the user the reload that actually fixes it, and naming
 * the duplicate-phone class tells them the number, not the code, is the
 * problem. Before that, a number already on another account rendered as a
 * generic error on the phone step and, for accounts the gateway exempted at
 * request time, as a wrong code on the verify step.
 */
export const getGatedRequestErrorCopyKey = (error: unknown): string => {
  switch (getGraphQLErrorCode(error)) {
    case GRAPHQL_ERROR_CODE.TURNSTILE_REJECTED:
      return AUTH_ERROR_COPY_KEY.robotCheck;
    case GRAPHQL_ERROR_CODE.FORBIDDEN:
      return AUTH_ERROR_COPY_KEY.blocked;
    case GRAPHQL_ERROR_CODE.PHONE_ALREADY_REGISTERED:
      return AUTH_ERROR_COPY_KEY.phoneAlreadyRegistered;
    default:
      return AUTH_ERROR_COPY_KEY.unknown;
  }
};

/**
 * Swaps a copy key Lokalise has not shipped for one it has.
 *
 * `public/locales` is gitignored and rebuilt from Lokalise at build time, so a
 * key this file names may not exist at runtime, and `t()` answers a miss by
 * returning the key. Rendering "common:common-auth-request-blocked" in an
 * error slot is worse than rendering a vaguer sentence that is at least a
 * sentence, so a miss costs precision, never legibility.
 *
 * Returns a KEY, not text, because most error slots here (`FormValidationError`,
 * `InputGroupErrorHelper`) run `t()` on whatever they are given. Handing them
 * text would translate it a second time. Callers that render the string
 * themselves wrap this in their own `t()`.
 *
 * `fallbackKey` lets a surface chain to a more specific shipped key before the
 * generic one; it is returned as given, not resolved again.
 */
export const resolveAuthErrorCopyKey = (
  key: string,
  t: (key: string) => string,
  fallbackKey: string = AUTH_ERROR_COPY_KEY.unknown
): string => {
  const text = t(key);

  /**
   * i18next answers a miss on "ns:key" with "key", the namespace dropped, so
   * comparing against the full key alone let a raw key through.
   */
  const bareKey = key.slice(key.indexOf(":") + 1);

  if (text.trim() && text !== key && text !== bareKey) {
    return key;
  }

  console.warn(`Missing translation for ${key}, falling back to generic copy`);

  /** Before Lokalise ships the robot-check copy, the blocked copy is the closer sentence. */
  return key === AUTH_ERROR_COPY_KEY.robotCheck
    ? AUTH_ERROR_COPY_KEY.blocked
    : fallbackKey;
};

/**
 * The gateway's `@rateLimit` rejection. It carries no code of its own (it
 * arrives as `INTERNAL_SERVER_ERROR`), so this message, from
 * `graphql-rate-limit-directive`'s default `onLimit`, is the only signal.
 * `verification.service.ts` throws the bare "Too many requests", with no time.
 */
const RATE_LIMIT_MESSAGE =
  /^Too many requests(?:, please try again in (\d+) seconds)?/;

/**
 * Copy for a rate-limit rejection, or `undefined` for any other error so the
 * caller falls through to its own copy.
 *
 * Signup is 5 an hour per IP, login 5 per 5 minutes, the phone code 5 an hour,
 * so the wait is shown in whole minutes, rounded up.
 *
 * Returns TEXT, not a key like the getters above: the wait has to be
 * interpolated, and the error slots run `t()` with no options of their own.
 * A second `t()` on a translated sentence hands it back unchanged, which the
 * slots already rely on for the mutations' own `message` text.
 *
 * Lokalise exports a plural key as a nested `{ one, other }` object, not
 * i18next's `_one`/`_other`, so the form is picked here, the same way
 * `pdp-seller-block-sales.one` is. `one` is exactly 1 in all six locales for
 * a count that never drops below 1.
 */
export const getRateLimitCopy = (
  error: unknown,
  t: (key: string, options?: { count: number }) => string
): string | undefined => {
  const match = getGraphQLErrorMessage(error)?.match(RATE_LIMIT_MESSAGE);

  if (!match) {
    return undefined;
  }

  const laterKey = resolveAuthErrorCopyKey(
    AUTH_ERROR_COPY_KEY.rateLimitedLater,
    t
  );
  const count = Math.ceil(Number(match[1]) / 60);

  if (!count) {
    return t(laterKey);
  }

  const key = resolveAuthErrorCopyKey(
    `${AUTH_ERROR_COPY_KEY.rateLimited}.${count === 1 ? "one" : "other"}`,
    key => t(key, { count }),
    laterKey
  );

  return t(key, { count });
};
